import type {
  AssistantManualDraftOverlayItem,
  AssistantMessage,
  AssistantSurveyAuthoringContext,
  AssistantThread,
} from '@prospero/shared/src/assistant/assistant-contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useCallback, useState } from 'react';
import { documentsArchiveQueryKeys } from '../../pages/documents-archive/query-keys';
import { haccpDashboardQueryKeys } from '../../pages/haccp-dashboard/query-keys';
import { blastChillingQueryKeys } from '../../pages/self-check/blast-chilling/query-keys';
import { cleaningQueryKeys } from '../../pages/self-check/cleaning/query-keys';
import { cookingTemperatureQueryKeys } from '../../pages/self-check/cooking-temperature/query-keys';
import { metalDetectionQueryKeys } from '../../pages/self-check/metal-detection/query-keys';
import { oilQueryKeys } from '../../pages/self-check/oil-change/query-keys';
import { pestControlQueryKeys } from '../../pages/self-check/pest-control/query-keys';
import { nonConformityQueryKeys } from '../../pages/self-check/shared/hooks/use-non-conformity-documents';
import { openNonConformityDaysQueryKeys } from '../../pages/self-check/shared/hooks/use-open-non-conformity-days';
import { tenantDocumentsQueryKeys } from '../../pages/self-check/shared/hooks/use-tenant-documents';
import { temperatureQueryKeys } from '../../pages/self-check/temperature/query-keys';
import type { IAssistantService } from '../../services/assistant/assistant.service';
import { AssistantService } from '../../services/assistant/assistant.service';
import { AssistantVoiceError } from '../../services/assistant/assistant-voice.error';
import {
  type ConsultantAssistantBlock,
  consultantAssistantBlockOf,
} from './consultant-assistant-block';
import { assistantKeys } from './query-keys';

interface UseAssistantOptions {
  /** Defer the thread fetch until the panel is actually open. */
  enabled: boolean;
  /**
   * Current pathname, when known. On a survey-studio route the thread read and
   * the "Nuova chat" reset must follow the authoring thread on the user's
   * CONSULTANT workspace (same tenant rule as the send), so the pageUrl rides
   * along; on every other route the backend ignores it.
   */
  pageUrl?: string;
}

interface VoiceSendFailure {
  code: AssistantVoiceError['code'];
  blob: Blob;
  pageUrl: string;
}

type SendRetryMode = 'resend' | 'reload';

const OPTIMISTIC_ID_PREFIX = 'optimistic-';

/**
 * Caches a confirmed action can outdate. Confirmation is what actually writes
 * to the HACCP register — a temperature reading, or a non-conformity document
 * attached to a self-check record — so the page behind the chat panel keeps
 * showing the pre-confirmation state until these are invalidated. The list is
 * flat instead of derived from the action kind because the confirm mutation
 * only carries an action id, and a confirmation is rare enough that a few
 * extra refetches cost less than threading the kind through every card.
 *
 * It must stay a superset of what the manual paths invalidate: the corrective
 * document uploaded from the record card already refreshes the open-days
 * calendar and the tenant document list (`use-non-conformity-documents.ts`),
 * and the same write done from chat has to refresh them too.
 */
const CONFIRMED_ACTION_QUERY_KEYS = [
  documentsArchiveQueryKeys.all,
  tenantDocumentsQueryKeys.all,
  temperatureQueryKeys.all,
  cleaningQueryKeys.all,
  oilQueryKeys.all,
  pestControlQueryKeys.all,
  blastChillingQueryKeys.all,
  cookingTemperatureQueryKeys.all,
  metalDetectionQueryKeys.all,
  nonConformityQueryKeys.all,
  openNonConformityDaysQueryKeys.all,
  haccpDashboardQueryKeys.all,
];

function optimisticUserMessage(text: string, pageUrl: string): AssistantMessage {
  return {
    id: `${OPTIMISTIC_ID_PREFIX}${crypto.randomUUID()}`,
    role: 'USER',
    text,
    citations: [],
    actions: [],
    pageUrl,
    createdAt: new Date().toISOString(),
  };
}

function mergeIntoThread(
  current: AssistantThread | undefined,
  responseMessages: AssistantMessage[],
): AssistantThread {
  const persisted = (current?.messages ?? []).filter(
    (message) => !message.id.startsWith(OPTIMISTIC_ID_PREFIX),
  );
  const persistedIds = new Set(persisted.map((message) => message.id));
  const additions = responseMessages.filter((message) => !persistedIds.has(message.id));
  return { messages: [...persisted, ...additions] };
}

/**
 * Owns the assistant thread query and the send mutation, including the
 * optimistic user bubble and rollback-on-failure. `sendMessage` captures the
 * caller's current pathname so every turn records where it was asked from.
 */
export function useAssistant({ enabled, pageUrl }: UseAssistantOptions) {
  const httpService = useInjection<IAssistantService>(AssistantService);
  const queryClient = useQueryClient();
  // pageUrl-scoped key: an authoring thread (CONSULTANT workspace) must never
  // be served from a page whose thread lives on the active tenant (and vice
  // versa). No pageUrl → the historical shared key.
  const queryKey = assistantKeys.thread(pageUrl);
  const [sendFailed, setSendFailed] = useState<SendRetryMode | null>(null);
  const [voiceFailed, setVoiceFailed] = useState<VoiceSendFailure | null>(null);
  // Consultant assistant without a studio or with the AI switched off: not retryable, own message.
  const [consultantAssistantBlock, setConsultantAssistantBlock] =
    useState<ConsultantAssistantBlock | null>(null);

  const threadQuery = useQuery({
    queryKey,
    queryFn: () => httpService.getThread(pageUrl),
    enabled,
    staleTime: 0,
  });

  const sendMutation = useMutation({
    mutationFn: (input: {
      text: string | null;
      pageUrl: string;
      manualDraftOverlay?: AssistantManualDraftOverlayItem[];
      surveyAuthoring?: AssistantSurveyAuthoringContext | null;
    }) => httpService.sendMessage(input),
    onMutate: ({ text, pageUrl }) => {
      setSendFailed(null);
      setConsultantAssistantBlock(null);
      const previous = queryClient.getQueryData<AssistantThread>(queryKey);
      // A slow GET started before the send must never clobber the post-send
      // thread; keep the optimistic bubble in the cache.
      void queryClient.cancelQueries({ queryKey }, { revert: false });
      if (text !== null) {
        queryClient.setQueryData<AssistantThread>(queryKey, {
          messages: [...(previous?.messages ?? []), optimisticUserMessage(text, pageUrl)],
        });
      }
      return previous?.messages ?? null;
    },
    onError: (error, input, messagesBeforeSend) => {
      setConsultantAssistantBlock(consultantAssistantBlockOf(error));
      if (messagesBeforeSend === null || messagesBeforeSend === undefined) {
        queryClient.removeQueries({ queryKey });
      } else {
        queryClient.setQueryData<AssistantThread>(queryKey, {
          messages: messagesBeforeSend,
        });
      }
      setSendFailed(input.text === null ? 'reload' : 'resend');
    },
    onSuccess: (response) => {
      queryClient.setQueryData<AssistantThread>(queryKey, (current) =>
        mergeIntoThread(current, response.messages),
      );
    },
  });

  const voiceMutation = useMutation({
    mutationFn: (input: { blob: Blob; pageUrl: string }) =>
      httpService.sendVoiceMessage(input.blob, input.pageUrl),
    onMutate: ({ blob, pageUrl }) => {
      setSendFailed(null);
      setVoiceFailed(null);
      const previous = queryClient.getQueryData<AssistantThread>(queryKey);
      void queryClient.cancelQueries({ queryKey }, { revert: false });
      return { blob, pageUrl, messagesBeforeSend: previous?.messages ?? null };
    },
    onError: (error, input, context) => {
      if (error instanceof AssistantVoiceError) {
        setVoiceFailed({ code: error.code, blob: input.blob, pageUrl: input.pageUrl });
        return;
      }
      setConsultantAssistantBlock(consultantAssistantBlockOf(error));
      if (context?.messagesBeforeSend === null) {
        queryClient.removeQueries({ queryKey });
      } else if (context) {
        queryClient.setQueryData<AssistantThread>(queryKey, {
          messages: context.messagesBeforeSend,
        });
      }
      setSendFailed('reload');
    },
    onSuccess: (response) => {
      queryClient.setQueryData<AssistantThread>(queryKey, (current) =>
        mergeIntoThread(current, response.messages),
      );
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => httpService.resetConversation(pageUrl),
    onMutate: () => {
      setSendFailed(null);
      setVoiceFailed(null);
      setConsultantAssistantBlock(null);
      void queryClient.cancelQueries({ queryKey }, { revert: false });
    },
    onSuccess: (response) => {
      queryClient.setQueryData<AssistantThread>(queryKey, response);
    },
  });

  const sendMessage = useCallback(
    (
      text: string | null,
      pageUrl: string,
      manualDraftOverlay?: AssistantManualDraftOverlayItem[],
      surveyAuthoring?: AssistantSurveyAuthoringContext | null,
    ) => sendMutation.mutateAsync({ text, pageUrl, manualDraftOverlay, surveyAuthoring }),
    [sendMutation],
  );

  const sendVoiceMessage = useCallback(
    (blob: Blob, pageUrl: string) => voiceMutation.mutateAsync({ blob, pageUrl }),
    [voiceMutation],
  );

  const retryVoice = useCallback(() => {
    if (!voiceFailed) {
      return Promise.resolve();
    }
    return sendVoiceMessage(voiceFailed.blob, voiceFailed.pageUrl);
  }, [sendVoiceMessage, voiceFailed]);

  const resetConversation = useCallback(() => resetMutation.mutateAsync(), [resetMutation]);

  const uploadAttachmentMutation = useMutation({
    mutationFn: (file: File) => httpService.uploadAttachment(file, pageUrl),
  });

  const confirmActionMutation = useMutation({
    // The NC document card reuses the project's existing
    // `ConfigureSignatureDialog` instead of drawing inline, which persists
    // (and invalidates) the signature itself through its own
    // `useUserSignature` save mutation — confirm never carries a payload.
    mutationFn: ({ actionId }: { actionId: string }) => httpService.confirmAction(actionId),
    onSuccess: () => {
      for (const queryKey of CONFIRMED_ACTION_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  const retry = useCallback(() => {
    setSendFailed(null);
    return threadQuery.refetch();
  }, [threadQuery]);

  const confirmActionMutateAsync = confirmActionMutation.mutateAsync;
  const confirmAction = useCallback(
    (actionId: string) => confirmActionMutateAsync({ actionId }),
    [confirmActionMutateAsync],
  );

  return {
    messages: threadQuery.data?.messages ?? [],
    isThreadLoading: threadQuery.isLoading,
    isSending: sendMutation.isPending || voiceMutation.isPending,
    isResetting: resetMutation.isPending,
    sendFailed: sendFailed !== null,
    consultantAssistantBlock:
      consultantAssistantBlock ?? consultantAssistantBlockOf(threadQuery.error),
    sendMessage,
    sendVoiceMessage,
    voiceFailed,
    retryVoice,
    resetConversation,
    retry,
    uploadAttachment: uploadAttachmentMutation.mutateAsync,
    confirmAction,
  };
}
